import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Helmet } from 'react-helmet';
import {
  MessageCircle,
  Shield,
  CheckCircle,
  ChevronRight,
  Star,
  Truck,
  Package,
  ArrowLeft,
  ShoppingBag,
} from 'lucide-react';
import { productsData } from '@/data/productsData';
import { getProductReviews, getAverageRating } from '@/data/reviewsData';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';

const ProductDetailPage = () => {
  const { slug } = useParams();
  const product = productsData.find((p) => p.slug === slug);
  const [selectedSize, setSelectedSize] = useState(0);
  const { addItem } = useCart();

  const reviews = useMemo(() => (product ? getProductReviews(product.slug) : []), [product]);
  const { avg: avgRating, count: reviewCount } = useMemo(
    () => (product ? getAverageRating(product.slug) : { avg: 4.8, count: 0 }),
    [product]
  );

  const relatedProducts = useMemo(() => {
    if (!product) return [];
    return productsData
      .filter((p) => p.category === product.category && p.id !== product.id)
      .slice(0, 4);
  }, [product]);

  if (!product) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex flex-col items-center justify-center pt-32">
          <h1 className="text-3xl font-bold text-foreground mb-4">Product Not Found</h1>
          <p className="text-muted-foreground mb-6">The product you&apos;re looking for doesn&apos;t exist.</p>
          <Button asChild>
            <Link to="/products">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Products
            </Link>
          </Button>
        </div>
      </Layout>
    );
  }

  const whatsappMessage = `Hi! I'm interested in ${product.name}${product.sizes?.[selectedSize] ? ` (${product.sizes[selectedSize]})` : ''}. Please share availability and pricing.`;

  return (
    <>
      <Helmet>
        <title>{`Buy ${product.name} - ${product.brand} | NutraFuel Supplements`}</title>
        <meta
          name="description"
          content={`Buy authentic ${product.name} by ${product.brand} at NutraFuel. ${product.description}. 100% genuine, lab-verified. Order on WhatsApp.`}
        />
        <link rel="canonical" href={`https://nutrafuel.in/products/${product.slug}`} />
        <meta property="og:title" content={`${product.name} - ${product.brand} | NutraFuel`} />
        <meta property="og:description" content={product.description} />
        <meta property="og:image" content={product.image} />
        <meta property="og:url" content={`https://nutrafuel.in/products/${product.slug}`} />
      </Helmet>

      <Layout>
        {/* Breadcrumb */}
        <div className="bg-muted/30 pt-24 pb-3 border-b border-border">
          <div className="container mx-auto px-4">
            <nav className="flex items-center text-sm text-muted-foreground" data-testid="breadcrumb">
              <Link to="/" className="hover:text-primary transition-colors">Home</Link>
              <ChevronRight className="w-3.5 h-3.5 mx-2" />
              <Link to="/products" className="hover:text-primary transition-colors">Products</Link>
              <ChevronRight className="w-3.5 h-3.5 mx-2" />
              <span className="text-foreground font-medium truncate max-w-[200px]">{product.name}</span>
            </nav>
          </div>
        </div>

        {/* Product Detail Section */}
        <section className="py-8 md:py-14 bg-background">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-14 max-w-7xl mx-auto">
              {/* Product Image */}
              <div className="relative" data-testid="product-image-section">
                <div className="sticky top-28 rounded-2xl overflow-hidden bg-muted/30 border border-border">
                  <div className="aspect-square">
                    <img
                      src={product.image}
                      alt={`${product.name} - ${product.brand} | Buy at NutraFuel`}
                      className="w-full h-full object-cover"
                      loading="eager"
                    />
                  </div>
                  {product.stock === 'In Stock' && (
                    <Badge className="absolute top-4 left-4 bg-green-600 text-white" data-testid="stock-badge">
                      <CheckCircle className="w-3.5 h-3.5 mr-1.5" />
                      {product.stock}
                    </Badge>
                  )}
                  {product.stock === 'Limited Stock' && (
                    <Badge className="absolute top-4 left-4 bg-amber-500 text-white" data-testid="stock-badge">
                      {product.stock}
                    </Badge>
                  )}
                  {product.popular && (
                    <Badge className="absolute top-4 right-4 bg-gold text-foreground" data-testid="popular-badge">
                      Popular
                    </Badge>
                  )}
                </div>
              </div>

              {/* Product Info */}
              <div data-testid="product-info-section">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm font-semibold text-gold uppercase tracking-wider">{product.brand}</span>
                  <span className="text-muted-foreground text-xs">|</span>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-3.5 h-3.5 ${
                          i < Math.round(avgRating) ? 'fill-gold text-gold' : 'fill-muted text-muted'
                        }`}
                      />
                    ))}
                    <span className="text-xs text-muted-foreground ml-1">
                      {avgRating}/5{reviewCount > 0 ? ` (${reviewCount} review${reviewCount > 1 ? 's' : ''})` : ''}
                    </span>
                  </div>
                </div>

                <h1 className="text-3xl sm:text-4xl font-display font-bold text-foreground leading-tight mb-3" data-testid="product-name">
                  {product.name}
                </h1>

                <p className="text-lg text-muted-foreground leading-relaxed mb-6" data-testid="product-description">
                  {product.longDescription}
                </p>

                {/* Key Features */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {product.features.map((feature) => (
                    <Badge key={feature} variant="secondary" className="px-3 py-1.5 text-sm font-medium">
                      {feature}
                    </Badge>
                  ))}
                </div>

                {/* Size Selector */}
                {product.sizes && product.sizes.length > 0 && (
                  <div className="mb-6" data-testid="size-selector">
                    <h3 className="text-sm font-semibold text-foreground mb-2.5">Size</h3>
                    <div className="flex flex-wrap gap-2">
                      {product.sizes.map((size, i) => (
                        <button
                          key={size}
                          onClick={() => setSelectedSize(i)}
                          className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                            selectedSize === i
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'border-border text-muted-foreground hover:border-primary/50'
                          }`}
                          data-testid={`size-option-${i}`}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 mb-8" data-testid="cta-buttons">
                  <Button
                    size="lg"
                    className="flex-1 text-base py-6"
                    onClick={() => {
                      addItem(product, product.sizes?.[selectedSize] || '');
                      toast.success(`${product.name} added to cart`);
                    }}
                    data-testid="add-to-cart-btn"
                  >
                    <ShoppingBag className="w-5 h-5 mr-2" />
                    Add to Cart
                  </Button>
                  <Button size="lg" variant="outline" className="flex-1 text-base py-6" asChild>
                    <a
                      href={`https://wa.me/9779824042067?text=${encodeURIComponent(whatsappMessage)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="order-whatsapp-btn"
                    >
                      <MessageCircle className="w-5 h-5 mr-2" />
                      Order on WhatsApp
                    </a>
                  </Button>
                </div>

                {/* Trust Indicators */}
                <div className="grid grid-cols-3 gap-3 p-4 rounded-xl bg-muted/40 border border-border" data-testid="trust-indicators">
                  <div className="flex flex-col items-center text-center gap-1.5">
                    <Shield className="w-5 h-5 text-green-600" />
                    <span className="text-xs font-medium text-muted-foreground">100% Authentic</span>
                  </div>
                  <div className="flex flex-col items-center text-center gap-1.5">
                    <Truck className="w-5 h-5 text-primary" />
                    <span className="text-xs font-medium text-muted-foreground">Fast Delivery</span>
                  </div>
                  <div className="flex flex-col items-center text-center gap-1.5">
                    <Package className="w-5 h-5 text-gold" />
                    <span className="text-xs font-medium text-muted-foreground">Sealed Pack</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits & Supplement Facts */}
        <section className="py-12 bg-muted/20 border-t border-border">
          <div className="container mx-auto px-4">
            <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-8">
              {/* Benefits */}
              <div data-testid="benefits-section">
                <h2 className="text-2xl font-display font-bold text-foreground mb-6">
                  Benefits
                </h2>
                <div className="space-y-3">
                  {product.benefits.map((benefit) => (
                    <div key={benefit} className="flex items-start gap-3 p-3 rounded-lg bg-background border border-border">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-foreground">{benefit}</span>
                    </div>
                  ))}
                </div>

                {/* Best For */}
                <div className="mt-6 p-4 rounded-xl bg-primary/5 border border-primary/20">
                  <span className="text-xs font-semibold text-primary uppercase tracking-wider">Best For</span>
                  <p className="text-foreground font-medium mt-1">{product.bestFor}</p>
                </div>
              </div>

              {/* Supplement Facts */}
              {product.supplementFacts && product.supplementFacts.length > 0 && (
                <div data-testid="supplement-facts-section">
                  <h2 className="text-2xl font-display font-bold text-foreground mb-6">
                    Supplement Facts
                  </h2>
                  <div className="rounded-xl border border-border overflow-hidden bg-background">
                    <div className="bg-foreground text-background px-5 py-3">
                      <span className="font-bold text-sm">Nutrition Information</span>
                    </div>
                    <div className="divide-y divide-border">
                      {product.supplementFacts.map((fact, index) => (
                        <div
                          key={fact.label}
                          className={`flex justify-between px-5 py-3 text-sm ${
                            index % 2 === 0 ? 'bg-background' : 'bg-muted/30'
                          }`}
                        >
                          <span className="text-muted-foreground">{fact.label}</span>
                          <span className="font-semibold text-foreground">{fact.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Ingredients */}
                  {product.ingredients && product.ingredients !== 'N/A' && (
                    <div className="mt-6" data-testid="ingredients-section">
                      <h3 className="text-lg font-bold text-foreground mb-3">Ingredients</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed p-4 rounded-xl bg-background border border-border">
                        {product.ingredients}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Customer Reviews */}
        <section className="py-12 bg-background border-t border-border" data-testid="reviews-section">
          <div className="container mx-auto px-4">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-display font-bold text-foreground">
                  Customer Reviews
                </h2>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-4 h-4 ${
                          i < Math.round(avgRating) ? 'fill-gold text-gold' : 'fill-muted text-muted'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-sm font-semibold text-foreground">{avgRating}/5</span>
                  {reviewCount > 0 && (
                    <span className="text-sm text-muted-foreground">({reviewCount} review{reviewCount > 1 ? 's' : ''})</span>
                  )}
                </div>
              </div>

              {reviews.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {reviews.map((review) => (
                    <div
                      key={review.id}
                      className="p-5 rounded-xl bg-muted/20 border border-border"
                      data-testid={`review-${review.id}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-sm font-bold text-primary">
                              {review.name.charAt(0)}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-foreground">{review.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(review.date).toLocaleDateString('en-IN', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                              })}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-0.5">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`w-3.5 h-3.5 ${
                                i < review.rating ? 'fill-gold text-gold' : 'fill-muted text-muted'
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed">{review.comment}</p>
                      <Badge variant="secondary" className="mt-3 text-xs">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Verified Purchase
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-10 rounded-xl bg-muted/10 border border-border">
                  <Star className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground font-medium">No reviews yet for this product</p>
                  <p className="text-sm text-muted-foreground mt-1">Be the first to review after purchase!</p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <section className="py-14 bg-background border-t border-border" data-testid="related-products-section">
            <div className="container mx-auto px-4">
              <div className="max-w-7xl mx-auto">
                <h2 className="text-2xl font-display font-bold text-foreground mb-8">
                  You May Also Like
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {relatedProducts.map((item) => (
                    <Link key={item.id} to={`/products/${item.slug}`} data-testid={`related-product-${item.id}`}>
                      <Card className="group overflow-hidden border-0 shadow-soft hover:shadow-elevated transition-all duration-300 hover:-translate-y-1">
                        <div className="aspect-square overflow-hidden bg-muted">
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                        </div>
                        <CardContent className="p-4">
                          <span className="text-xs text-gold font-medium uppercase tracking-wider">{item.brand}</span>
                          <h3 className="font-bold text-foreground text-sm mt-1 line-clamp-2">{item.name}</h3>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}
      </Layout>
    </>
  );
};

export default ProductDetailPage;
